import type { AxiosError } from 'axios'
import i18next from 'i18next'
import { detailValues } from '@/domains/shared/utils/apiErrorDetails'

export interface ApiErrorResponse {
  success: false
  error: string
  code?: string
  /** Structured facts behind a refusal, per code. */
  details?: unknown
}

function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as Record<string, unknown>).error === 'string'
  )
}

/** The API's own error payload off an axios error, or null for a non-API failure. */
export function readApiError(error: AxiosError | Error): ApiErrorResponse | null {
  if ('response' in error && error.response) {
    const { data } = error.response as { data: unknown }
    if (isApiErrorResponse(data)) {
      return data
    }
  }
  return null
}

/**
 * Codes the API uses as a bucket rather than a specific failure. Their catalog
 * entries are deliberately vague, so the server's own message wins when it has
 * one. Entity-specific codes stay out on purpose (catalog-first keeps them
 * localized). See apps/web/CLAUDE.md, "Error messages".
 */
const GENERIC_CODES = new Set([
  'VALIDATION_ERROR',
  'BAD_REQUEST',
  'CONFLICT',
  'FORBIDDEN',
  'INVALID_INPUT',
  'NOT_FOUND',
  'DUPLICATE_RESOURCE',
  'INVALID_STATUS_TRANSITION',
  // Names how many users still hold the role; a fixed translation would drop the count.
  'ROLE_IN_USE',
])

/**
 * The text for an API error payload - the same resolution the toast uses, so
 * a refusal recorded on an entity (a campaign's Activation Failure) reads
 * exactly as it did when it was refused live.
 *
 * A catalogued code that carries details renders `errors.<CODE>_DETAILED`
 * with them when that key exists; without usable details it falls
 * back to the plain `errors.<CODE>` text, so a payload the web does not
 * recognise never blanks the message.
 */
export function resolveApiErrorText({ error, code, details }: ApiErrorResponse): string {
  if (code) {
    const i18nKey = `errors.${code}`
    if (GENERIC_CODES.has(code) && error) {
      return error
    }
    if (i18next.exists(i18nKey)) {
      const values = details === undefined ? null : detailValues(code, details)
      if (values && i18next.exists(`${i18nKey}_DETAILED`)) {
        return i18next.t(`${i18nKey}_DETAILED`, values)
      }
      return i18next.t(i18nKey)
    }
    // biome-ignore lint/suspicious/noConsole: an uncatalogued code is a missing-translation signal worth surfacing to devs
    console.error('[api] uncatalogued error code', { code, error })
    return error ? `${error} (${code})` : code
  }
  return error
}

/**
 * Extracts a user-friendly error message from an API error response, in an order
 * that never masks the real error. The Axios message is never rendered.
 * See apps/web/CLAUDE.md, "Error messages".
 */
export function getApiErrorMessage(error: AxiosError | Error): string {
  const data = readApiError(error)
  if (data) {
    const text = resolveApiErrorText(data)
    if (text) {
      return text
    }
  }
  // biome-ignore lint/suspicious/noConsole: an unmapped failure must stay debuggable
  console.error('[api] non-API error', error)
  return i18next.t('errors.UNKNOWN')
}
